import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import { aiConfigItemMessages } from '@atlaskit/editor-common/ai-messages';
import {
	ACTION,
	ACTION_SUBJECT,
	ACTION_SUBJECT_ID,
	EVENT_TYPE,
} from '@atlaskit/editor-common/analytics';
import {
	AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD,
	AI_MENU_SECTION,
	AI_MENU_SECTION_RANK,
	AI_REMIX_MENU_ITEM,
} from '@atlaskit/editor-common/block-menu';
import { useSharedPluginStateWithSelector } from '@atlaskit/editor-common/hooks';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { RegisterBlockMenuComponent } from '@atlaskit/editor-plugin-block-menu';
import type { EditorState } from '@atlaskit/editor-prosemirror/state';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { ToolbarDropdownItem } from '@atlaskit/editor-toolbar';
import AiGenerativeRemixIcon from '@atlaskit/icon-lab/core/ai-generative-remix';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { expVal } from '@atlaskit/platform-feature-experiments/exp-val';
import { isExperimentEnabled } from '@atlaskit/platform-feature-experiments/is-experiment-enabled';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { TriggerProps } from '@atlaskit/popup/types';
import { expValEquals } from '@atlaskit/tmp-editor-statsig/exp-val-equals';
import { expValEqualsNoExposure } from '@atlaskit/tmp-editor-statsig/exp-val-equals-no-exposure';
import { ExperienceName } from '@atlassian/conversation-assistant-instrumentation/constants/experience-tracker';
import { startRemixCreateExperience } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-create-collect';
import { getRemixFeatureArea } from '@atlassian/conversation-assistant-instrumentation/controllers/experience-tracker/remix-feature-area';
import { useAIMateExperienceTracker } from '@atlassian/conversation-assistant-instrumentation/main';
import { RemixContainer } from '@atlassian/malleable-ui-components/remix';
import type { RemixOptionSelectionParameters } from '@atlassian/malleable-ui-components/remix/remix-overlay/types';

import type {
	MalleableUiPlugin,
	RemixHiddenOptionsResolver as RemixHiddenOptionsResolverType,
} from '../../malleableUiPluginType';
import { EmptyRemixHiddenOptionsResolver } from '../components/RemixHiddenOptionsResolver';
import { messages } from '../messages';
import { isRemixInlineDropdownVariant, shouldShowRemixForSelectionContext } from '../remix-config';
import { hasAiEditInProgressState } from '../utils/is-ai-edit-in-progress';

const hasRemixInProgressState = (
	state:
		| {
				isRemixModalOpen?: boolean;
				isThinking?: boolean;
		  }
		| undefined,
): boolean => Boolean(state?.isRemixModalOpen || state?.isThinking);

const RemixBlockMenuItem = ({
	api,
	remixHiddenOptionsResolver: RemixHiddenOptionsResolver = EmptyRemixHiddenOptionsResolver,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType;
}) => {
	const { formatMessage } = useIntl();
	const experienceTracker = useAIMateExperienceTracker();
	const [isDropdownOpen, setIsDropdownOpen] = useState(false);
	const isInlineDropdownVariant = isRemixInlineDropdownVariant();
	const { aiEditInProgress, contentId, isRemixUpdate, remixInProgress } =
		useSharedPluginStateWithSelector(
			api,
			['aiExperience', 'contextIdentifier', 'malleableUi'],
			(states) => ({
				aiEditInProgress: hasAiEditInProgressState(states.aiExperienceState),
				contentId: states.contextIdentifierState?.contextIdentifierProvider?.objectId,
				isRemixUpdate: states.malleableUiState?.isUpdate ?? false,
				remixInProgress: hasRemixInProgressState(states.malleableUiState),
			}),
		);
	const isDisabled =
		(aiEditInProgress || remixInProgress) && isExperimentEnabled('cc_maui_polish_changes_batch_5');

	// Fire experiment exposure on mount — registration uses expValEqualsNoExposure
	// to avoid over-counting on every editor load. The exposure is recorded here
	// when the Remix button is actually visible to the user.
	useEffect(() => {
		expValEquals('platform_editor_remix_in_block_menu', 'isEnabled', true);
	}, []);

	const fireClickAnalytics = useCallback(() => {
		api?.analytics?.actions.fireAnalyticsEvent({
			action: ACTION.CLICKED,
			actionSubject: ACTION_SUBJECT.BUTTON,
			actionSubjectId: ACTION_SUBJECT_ID.AI_REMIX_BUTTON,
			eventType: EVENT_TYPE.UI,
			attributes: {
				entryPoint: 'editor',
				triggeredFrom: 'blockMenu',
			},
		});
	}, [api]);

	const openRemix = useCallback(
		(preselection?: RemixOptionSelectionParameters) => {
			// Close block menu and clear selection highlight
			api?.core?.actions.execute(({ tr }) => {
				api?.blockControls?.commands?.stopPreservingSelection()({ tr });
				api?.blockControls?.commands?.toggleBlockMenu({ closeMenu: true })({ tr });
				api?.selection?.commands?.clearBlockSelection()({ tr });
				return tr;
			});

			// Start experience tracking and open remix modal.
			// malleableUi is guaranteed to exist here — this component is always
			// registered by malleableUiPlugin itself.
			const openRemixModal = api.malleableUi?.commands.openRemixModal({
				metadata: {
					interactionSource: 'remixModalFromBlockMenu',
					...(preselection && {
						preselectedType: preselection.type,
						preselectedSubType: preselection.subType,
						...(preselection.userPrompt && {
							preselectedUserPrompt: preselection.userPrompt,
						}),
					}),
				},
				source: 'remixModalFromBlockMenu',
			});

			if (openRemixModal) {
				const featureArea = getRemixFeatureArea({});
				experienceTracker.start({
					name: ExperienceName.REMIX_START,
					id: ExperienceName.REMIX_START,
					attributes: {
						entryPoint: 'editor',
						remixSource: 'blockMenu',
						...(featureArea !== undefined && { featureArea }),
					},
				});
				startRemixCreateExperience({
					experienceTracker,
					attributes: { entryPoint: 'editor', remixSource: 'blockMenu' },
				});
				api.core.actions.execute(openRemixModal);
			}
		},
		[api, experienceTracker],
	);

	const handleClick = useCallback(() => {
		if (isDisabled) {
			return;
		}
		fireClickAnalytics();
		expVal('cc-ce-maui-remix-menu-multivariant', 'cohort', 'control');
		expVal('cc-ce-maui-remix-menu-multivariant-aa', 'variant', 'control');

		if (isInlineDropdownVariant) {
			// Keep the block menu open while the live Remix trigger owns its nested dropdown.
			// The block menu is closed by `openRemix` once an option is committed.
			setIsDropdownOpen((isOpen) => !isOpen);
			return;
		}
		openRemix();
	}, [fireClickAnalytics, isDisabled, isInlineDropdownVariant, openRemix]);

	const closeDropdown = useCallback(() => setIsDropdownOpen(false), []);

	const handleOptionSelect = useCallback(
		(selection: RemixOptionSelectionParameters) => {
			closeDropdown();
			openRemix(selection);
		},
		[closeDropdown, openRemix],
	);

	const disabledTooltip = isDisabled
		? aiEditInProgress && (!remixInProgress || isRemixUpdate)
			? formatMessage(messages.editInProgressTooltip)
			: formatMessage(messages.remixInProgressTooltip)
		: undefined;

	const renderMenuItem = (triggerProps?: TriggerProps) => (
		<ToolbarDropdownItem
			triggerRef={triggerProps?.ref}
			elemBefore={<AiGenerativeRemixIcon label="" size="small" />}
			elemAfter={isInlineDropdownVariant ? <ChevronRightIcon label="" size="small" /> : undefined}
			hasNestedDropdownMenu={isInlineDropdownVariant}
			onClick={handleClick}
			isSelected={isInlineDropdownVariant ? isDropdownOpen : undefined}
			isDisabled={isDisabled}
			title={disabledTooltip}
		>
			{formatMessage(aiConfigItemMessages.remixTitle)}
		</ToolbarDropdownItem>
	);

	if (!isInlineDropdownVariant) {
		return renderMenuItem();
	}

	return (
		<RemixHiddenOptionsResolver enabled contentId={contentId}>
			{(hiddenOptions) => (
				<RemixContainer
					hiddenOptions={hiddenOptions}
					presentation="popup"
					trigger={renderMenuItem}
					popupPlacement="right-start"
					// Match the default 8px distance used by the Change format nested dropdown.
					popupOffset={[0, 8]}
					isPopupOpen={isDropdownOpen}
					isThinking={false}
					onClose={closeDropdown}
					onOptionSelect={handleOptionSelect}
				/>
			)}
		</RemixHiddenOptionsResolver>
	);
};

/**
 * Creates block menu registration components for the Remix action.
 *
 * Registered by editor-plugin-malleable-ui (which owns the openRemixModal command)
 * when the platform_editor_remix_in_block_menu experiment is enabled.
 * The AI plugin hides its polish/improve-formatting action when this experiment is on,
 * so the Remix item effectively replaces it.
 */
const createRemixBlockMenuItemComponent = (
	api: ExtractInjectionAPI<MalleableUiPlugin>,
	remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType,
) => {
	const RemixBlockMenuItemWrapper = () => (
		<RemixBlockMenuItem api={api} remixHiddenOptionsResolver={remixHiddenOptionsResolver} />
	);
	RemixBlockMenuItemWrapper.displayName = 'RemixBlockMenuItemWrapper';
	return RemixBlockMenuItemWrapper;
};

/**
 * Per-editor-state cache for the Remix item's `getTextInfo` lookup.
 *
 * The block-menu renderer probes `isHidden` several times during a single render pass
 * (once for the parent section and again for the item), so without memoization the
 * `getTextInfo` doc walk would run repeatedly for the same selection. The cache is keyed
 * by the editor `state` identity, which changes on every transaction, so it invalidates
 * itself automatically.
 */
type RemixTextLengthCache = {
	state: EditorState | null;
	textLength: number | null;
};

const getCachedTextLength = (
	api: ExtractInjectionAPI<MalleableUiPlugin>,
	editorView: EditorView,
	cache: RemixTextLengthCache,
): number | null => {
	const { state } = editorView;
	if (cache.state === state) {
		return cache.textLength;
	}
	const textLength = api?.blockControls?.actions.getTextInfo(editorView)?.textLength ?? null;
	cache.state = state;
	cache.textLength = textLength;
	return textLength;
};

export const getRemixBlockMenuComponents = ({
	api,
	getEditorView,
	remixHiddenOptionsResolver,
}: {
	api: ExtractInjectionAPI<MalleableUiPlugin>;
	getEditorView: () => EditorView | undefined;
	remixHiddenOptionsResolver?: RemixHiddenOptionsResolverType;
}): RegisterBlockMenuComponent[] => {
	const textLengthCache: RemixTextLengthCache = { state: null, textLength: null };

	return [
		{
			type: 'block-menu-item' as const,
			key: `ai-${AI_REMIX_MENU_ITEM.key}`,
			parent: {
				type: 'block-menu-section' as const,
				key: AI_MENU_SECTION.key,
				rank: (AI_MENU_SECTION_RANK as Record<string, number>)[AI_MENU_SECTION.key],
			},
			component: createRemixBlockMenuItemComponent(api, remixHiddenOptionsResolver),
			isHidden: () => {
				if (
					!expValEqualsNoExposure('platform_editor_ai_blockmenu_integration', 'isEnabled', true)
				) {
					return true;
				}
				const editorView = getEditorView();
				const menuTriggerByNode =
					api?.blockControls?.sharedState?.currentState()?.menuTriggerByNode;
				const triggerPos = menuTriggerByNode?.rootPos ?? menuTriggerByNode?.pos;
				const triggeredNode =
					editorView && triggerPos !== undefined
						? editorView.state.doc.nodeAt(triggerPos)
						: undefined;

				if (
					triggeredNode?.type.name === 'extension' &&
					shouldShowRemixForSelectionContext({ node: triggeredNode }) &&
					fg('maui_remixable_page_native_embeds')
				) {
					return false;
				}

				const textLength = editorView
					? getCachedTextLength(api, editorView, textLengthCache)
					: null;
				// When context is unavailable, default to visible (matches AI plugin behaviour)
				if (textLength === null) {
					return false;
				}
				return textLength < AI_BLOCK_MENU_LARGE_TEXT_THRESHOLD;
			},
		},
	];
};
